import * as Sentry from '@sentry/react'
import { ConnectError } from '@connectrpc/connect'
import { toast } from 'sonner'
import { ErrorDetailSchema } from '@/gen/cortado/v1/errdetails/errdetails_pb'

type ErrorContext = {
  feature?: string
  kind?: 'query' | 'mutation' | 'render'
  [k: string]: unknown
}

type ErrorCopy = {
  title: string
  description: string
  action?: { label: string; href: string }
}

const reportedErrors = new WeakSet<object>()

function readReason(err: unknown): string | undefined {
  if (!(err instanceof ConnectError)) return undefined
  // Details parsed off the wire are Any-wrapped protobuf bytes, not plain
  // objects — findDetails() is the only way to decode them.
  for (const detail of err.findDetails(ErrorDetailSchema)) {
    if (detail.reason) return detail.reason
  }
  return undefined
}

const REASON_MAP: Record<string, ErrorCopy> = {
  stripe_not_connected: {
    title: 'Connect Stripe first',
    description: 'You need to connect Stripe before creating paid quotes.',
    action: { label: 'Connect Stripe', href: '/settings/integrations/stripe' },
  },
  gmail_token_expired: {
    title: 'Reconnect Gmail',
    description: 'Your Gmail connection expired. Reconnect to keep sending quotes.',
    action: { label: 'Reconnect Gmail', href: '/communications?section=connect-gmail' },
  },
  supabase_create_failed: {
    title: "Couldn't create user",
    description: 'We had trouble creating this user. Please try again.',
  },
  db_create_failed: {
    title: "Couldn't save changes",
    description: 'We had trouble saving. Please try again.',
  },
}

const FALLBACK_COPY: ErrorCopy = {
  title: 'Something went wrong',
  description: 'Please try again. If the problem persists, contact support.',
}

export function reportError(err: unknown, ctx?: ErrorContext): void {
  if (err && typeof err === 'object') {
    if (reportedErrors.has(err)) return
    reportedErrors.add(err)
  }
  const reason = readReason(err)
  Sentry.captureException(err, {
    tags: {
      ...(ctx?.feature ? { feature: ctx.feature } : {}),
      ...(ctx?.kind ? { kind: ctx.kind } : {}),
      ...(reason ? { reason } : {}),
      ...(typeof ctx?.apiService === 'string'
        ? { api_service: ctx.apiService }
        : {}),
      ...(typeof ctx?.apiMethod === 'string'
        ? { api_method: ctx.apiMethod }
        : {}),
      ...(typeof ctx?.backendRequestId === 'string'
        ? { backend_request_id: ctx.backendRequestId }
        : {}),
      ...(typeof ctx?.backendTraceId === 'string'
        ? { backend_trace_id: ctx.backendTraceId }
        : {}),
    },
    extra: ctx,
  })
}

export function getErrorMessage(err: unknown): ErrorCopy {
  const reason = readReason(err)
  if (reason && reason in REASON_MAP) return REASON_MAP[reason]
  return FALLBACK_COPY
}

export function mutationErrorHandler(ctx?: ErrorContext): (err: unknown) => void {
  return (err: unknown) => {
    reportError(err, { kind: 'mutation', ...ctx })
    const copy = getErrorMessage(err)
    toast.error(copy.title, {
      description: copy.description,
      ...(copy.action
        ? {
            action: {
              label: copy.action.label,
              onClick: () => {
                window.location.href = copy.action!.href
              },
            },
          }
        : {}),
    })
  }
}
